import React from "react";
import {
    FaBookOpen,
    FaBookmark,
    FaClipboardList,
    FaPlay,
    FaGraduationCap,
    FaChalkboardTeacher,
    FaTimes,
    FaLock,
    // Subject / form-control icons
    FaBook,
    FaSquareRootAlt,
    FaAtom,
    FaFlask,
    FaDna,
    FaSeedling,
    FaChartLine,
    FaStore,
    FaCoins,
    FaMountain,
    FaLandmark,
    FaScroll,
    FaLaptopCode,
    FaBible,
    FaMosque,
    FaPalette,
    FaMusic,
    FaHeartbeat,
    FaUtensils,
    FaLanguage,
    FaComments,
    FaDraftingCompass,
    FaUsers,
    FaMicroscope,
    FaSearch,
    FaCheck,
    FaChevronDown,
    FaHashtag,
    FaLayerGroup,
} from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { SecureMedia } from "../protected";

// Shared building blocks for the student-facing marketplace (feed, channel,
// watch). Keeping the type styling, avatars and thumbnail tiles here means the
// three pages stay visually consistent.

// Per content-type: label + icon + the gradient used for auto-generated tiles
// when a teacher didn't upload a cover image.
export const TYPE_META = {
    lesson_notes: { label: "Lesson Notes", Icon: FaBookOpen, gradient: "from-blue-500 to-indigo-600" },
    revision_guide: { label: "Revision Guide", Icon: FaBookmark, gradient: "from-amber-500 to-orange-600" },
    practice_test: { label: "Practice Test", Icon: FaClipboardList, gradient: "from-emerald-500 to-teal-600" },
    video: { label: "Video", Icon: FaPlay, gradient: "from-rose-500 to-red-600" },
    exam_prep: { label: "Exam Prep Pack", Icon: FaGraduationCap, gradient: "from-violet-500 to-purple-600" },
};

export const typeMeta = (type) =>
    TYPE_META[type] || { label: type || "Content", Icon: FaBookOpen, gradient: "from-gray-500 to-gray-700" };

export const TYPE_LABELS = Object.fromEntries(
    Object.entries(TYPE_META).map(([k, v]) => [k, v.label])
);

// Per-subject icon + colour so a bare subject string renders as a friendly,
// recognisable chip instead of plain grey text. Matched by keyword, most
// specific first, with a neutral fallback. `chip` holds a full Tailwind
// bg+text pair (written out so JIT never purges it).
const SUBJECT_VISUALS = [
    [/further|add(itional)?\s*math|f[.\s-]?math/, { Icon: FaSquareRootAlt, chip: "bg-indigo-100 text-indigo-700" }],
    [/math|arithmetic|algebra|geometr|calculus|quantitative/, { Icon: FaSquareRootAlt, chip: "bg-indigo-50 text-indigo-700" }],
    [/further\s*sci|basic\s*sci|integrated\s*sci|general\s*sci|^science|\bscience\b/, { Icon: FaMicroscope, chip: "bg-cyan-50 text-cyan-700" }],
    [/physics/, { Icon: FaAtom, chip: "bg-cyan-50 text-cyan-700" }],
    [/chemistry/, { Icon: FaFlask, chip: "bg-emerald-50 text-emerald-700" }],
    [/biolog/, { Icon: FaDna, chip: "bg-green-50 text-green-700" }],
    [/agric|farm/, { Icon: FaSeedling, chip: "bg-lime-50 text-lime-700" }],
    [/econom/, { Icon: FaChartLine, chip: "bg-amber-50 text-amber-700" }],
    [/account|book\s*keep|financial/, { Icon: FaCoins, chip: "bg-yellow-50 text-yellow-700" }],
    [/commerce|market|business|trade|entrepreneur/, { Icon: FaStore, chip: "bg-orange-50 text-orange-700" }],
    [/geograph/, { Icon: FaMountain, chip: "bg-teal-50 text-teal-700" }],
    [/govern|civic|politic/, { Icon: FaLandmark, chip: "bg-blue-50 text-blue-700" }],
    [/histor/, { Icon: FaScroll, chip: "bg-stone-100 text-stone-700" }],
    [/comput|ict|data\s*process|coding|software|information tech/, { Icon: FaLaptopCode, chip: "bg-violet-50 text-violet-700" }],
    [/christ|crs|crk|bible|religious knowledge/, { Icon: FaBible, chip: "bg-rose-50 text-rose-700" }],
    [/islam|irs|arabic|quran/, { Icon: FaMosque, chip: "bg-green-100 text-green-800" }],
    [/technical\s*draw|draught|drafting/, { Icon: FaDraftingCompass, chip: "bg-slate-100 text-slate-700" }],
    [/\bart\b|fine art|drawing|creative|craft/, { Icon: FaPalette, chip: "bg-pink-50 text-pink-700" }],
    [/music/, { Icon: FaMusic, chip: "bg-fuchsia-50 text-fuchsia-700" }],
    [/health|physical|\bphe\b|p\.?e\.?$|sport/, { Icon: FaHeartbeat, chip: "bg-red-50 text-red-700" }],
    [/home\s*econ|food|cater|nutrition|hospitality/, { Icon: FaUtensils, chip: "bg-orange-100 text-orange-700" }],
    [/french|spanish|german|\blang/, { Icon: FaLanguage, chip: "bg-sky-100 text-sky-700" }],
    [/yoruba|igbo|hausa|literature/, { Icon: FaComments, chip: "bg-purple-50 text-purple-700" }],
    [/english|grammar|oral|verbal|reading/, { Icon: FaBookOpen, chip: "bg-sky-50 text-sky-700" }],
    [/social/, { Icon: FaUsers, chip: "bg-teal-50 text-teal-700" }],
];

const DEFAULT_SUBJECT_VISUAL = { Icon: FaBook, chip: "bg-gray-100 text-gray-600" };

// Resolve a subject name → { Icon, chip }. Never throws; always returns a value.
export const subjectVisual = (subject) => {
    const s = String(subject || "").toLowerCase().trim();
    if (!s) return DEFAULT_SUBJECT_VISUAL;
    for (const [re, v] of SUBJECT_VISUALS) if (re.test(s)) return v;
    return DEFAULT_SUBJECT_VISUAL;
};

// Small rounded icon tile for a subject (used in pickers + list rows).
export const SubjectIcon = ({ subject, size = 34, iconSize = 15, className = "" }) => {
    const { Icon, chip } = subjectVisual(subject);
    return (
        <span
            style={{ width: size, height: size }}
            className={`inline-flex items-center justify-center rounded-lg shrink-0 ${chip} ${className}`}
        >
            <Icon size={iconSize} />
        </span>
    );
};

// Backend returns absolute URLs, but guard against relative ones just in case.
export const toAbsUrl = (url) => {
    if (!url) return null;
    return url.startsWith("http") ? url : `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Compact count, e.g. 1200 -> "1.2K", used for subscriber counts.
export const formatCount = (n) => {
    const num = Number(n) || 0;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    return String(num);
};

// Small pill badges listing an item's (or a teacher's) subjects and classes,
// shown across the feed / channel / watch views so a student sees exactly what
// a piece of content is for. Subjects are blue, classes grey. `max` caps how
// many show before collapsing the rest into a "+N" pill.
export const TagBadges = ({ subjects = [], classes = [], className = "", max }) => {
    const subs = (Array.isArray(subjects) ? subjects : []).map((s) => ({ label: s, kind: "subject" }));
    const cls = (Array.isArray(classes) ? classes : []).map((c) => ({ label: c, kind: "class" }));
    let items = [...subs, ...cls];
    if (!items.length) return null;

    let extra = 0;
    if (max && items.length > max) {
        extra = items.length - max;
        items = items.slice(0, max);
    }

    return (
        <div className={`flex flex-wrap gap-1 ${className}`}>
            {items.map((it, i) => (
                <span
                    key={`${it.kind}-${it.label}-${i}`}
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        it.kind === "subject" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"
                    }`}
                >
                    {it.label}
                </span>
            ))}
            {extra > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    +{extra}
                </span>
            )}
        </div>
    );
};

// Round teacher avatar: profile image if present, else initials on a brand tile.
export const TeacherAvatar = ({ name = "", src, size = 40, className = "" }) => {
    const [broken, setBroken] = React.useState(false);
    const url = toAbsUrl(src);
    const initials =
        name
            .split(" ")
            .map((p) => p[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase() || "T";
    const style = { width: size, height: size };

    if (url && !broken) {
        return (
            <img
                src={url}
                alt={name}
                style={style}
                onError={() => setBroken(true)}
                className={`rounded-full object-cover bg-gray-100 shrink-0 ${className}`}
            />
        );
    }
    return (
        <div
            style={{ ...style, fontSize: size * 0.4 }}
            className={`rounded-full bg-blue600 text-white flex items-center justify-center font-semibold shrink-0 ${className}`}
        >
            {initials}
        </div>
    );
};

// 16:9 content thumbnail. Uses the teacher's uploaded cover when available,
// otherwise an auto-generated gradient tile from the content type. Videos always
// get a play-button overlay (YouTube-style), even over a real cover image.
export const ContentThumb = ({ type, thumbnailUrl, title = "", className = "", rounded = "rounded-xl" }) => {
    const [broken, setBroken] = React.useState(false);
    const meta = typeMeta(type);
    const { Icon } = meta;
    const cover = toAbsUrl(thumbnailUrl);
    const isVideo = type === "video";

    return (
        <div className={`relative aspect-video w-full overflow-hidden ${rounded} bg-gray-100 ${className}`}>
            {cover && !broken ? (
                <img
                    src={cover}
                    alt={title}
                    onError={() => setBroken(true)}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : (
                <div className={`absolute inset-0 bg-gradient-to-br ${meta.gradient} flex flex-col items-center justify-center text-white p-3`}>
                    <Icon size={30} className="opacity-90" />
                    <span className="mt-2 text-[11px] font-semibold uppercase tracking-wide opacity-90">{meta.label}</span>
                    {title && (
                        <span className="mt-1 text-xs font-medium text-center line-clamp-2 opacity-95">{title}</span>
                    )}
                </div>
            )}

            {/* Type badge (bottom-right, like a video duration pill) */}
            <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                {meta.label}
            </span>

            {isVideo && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-black/55 rounded-full w-12 h-12 flex items-center justify-center">
                        <FaPlay className="text-white ml-0.5" size={18} />
                    </span>
                </div>
            )}
        </div>
    );
};

// Friendly single-select SUBJECT picker — replaces the old bland
// <input list="…"> datalist. Shows the chosen subject as a coloured icon chip
// and opens a searchable dropdown of subjects (each with its own icon/colour).
// Still allows a custom subject (the bank can be thin), so nothing is lost vs
// the free-text datalist. `subjects` is the flat list from /api/quiz/metadata.
export const SubjectPicker = ({
    label, hint, subjects = [], value = "", onChange,
    placeholder = "Choose a subject", allowCustom = true, required = false,
}) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const needle = query.trim().toLowerCase();
    const matches = subjects.filter((s) => s.toLowerCase().includes(needle));
    const canAddCustom =
        allowCustom && needle && !subjects.some((s) => s.toLowerCase() === needle);

    const pick = (s) => { onChange(s); setQuery(""); setOpen(false); };

    return (
        <div ref={ref} className="relative">
            {label && (
                <p className="text-sm font-medium text-gray-700 mb-1">
                    {label}{required && <span className="text-red-500"> *</span>}
                </p>
            )}
            {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center gap-2.5 rounded-xl border p-2 text-left transition ${
                    open ? "border-blue-400 ring-2 ring-blue-100" : "border-gray-200 hover:border-gray-300"
                }`}
            >
                {value ? (
                    <>
                        <SubjectIcon subject={value} />
                        <span className="font-medium text-gray-800 truncate flex-1">{value}</span>
                    </>
                ) : (
                    <>
                        <span className="w-[34px] h-[34px] inline-flex items-center justify-center rounded-lg bg-gray-100 text-gray-400 shrink-0">
                            <FaBookOpen size={15} />
                        </span>
                        <span className="text-gray-400 flex-1">{placeholder}</span>
                    </>
                )}
                <FaChevronDown size={12} className={`text-gray-400 transition ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="absolute z-30 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                        <FaSearch size={12} className="text-gray-400 shrink-0" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") setOpen(false);
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (matches.length) pick(matches[0]);
                                    else if (canAddCustom) pick(query.trim());
                                }
                            }}
                            placeholder="Search subjects…"
                            className="w-full text-sm outline-none bg-transparent"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                        {matches.map((s) => {
                            const active = s === value;
                            return (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => pick(s)}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 ${active ? "bg-blue-50" : ""}`}
                                >
                                    <SubjectIcon subject={s} size={30} iconSize={13} />
                                    <span className={`text-sm truncate flex-1 ${active ? "text-blue-700 font-semibold" : "text-gray-700"}`}>{s}</span>
                                    {active && <FaCheck size={12} className="text-blue-600 shrink-0" />}
                                </button>
                            );
                        })}
                        {canAddCustom && (
                            <button
                                type="button"
                                onClick={() => pick(query.trim())}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 border-t border-gray-100"
                            >
                                <SubjectIcon subject={query} size={30} iconSize={13} />
                                <span className="text-sm text-gray-700 truncate flex-1">
                                    Use “<span className="font-semibold">{query.trim()}</span>”
                                </span>
                            </button>
                        )}
                        {!matches.length && !canAddCustom && (
                            <p className="px-3 py-4 text-center text-xs text-gray-400">No subjects found.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Row of selectable class pills. The options are the teacher's OWN classes
// (their "What you teach" profile), so only classes they teach are offered.
// Two profile-aware states:
//   * no classes in the profile yet  -> an amber hint pointing to the profile.
//   * the current value isn't one of the teacher's classes (e.g. a legacy
//     lesson tagged with a class later dropped from the profile) -> the value
//     still shows as an amber "not in profile" pill (so it's visible and
//     clearable) with a note, per the "say it's not part of your profile" ask.
export const ClassPills = ({ label, classes = [], value = "", onChange, required = false, hint }) => {
    const offProfile = !!value && !classes.includes(value);
    const empty = classes.length === 0;
    return (
        <div>
            {label && (
                <p className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                    <FaLayerGroup className="text-gray-400" size={12} /> {label}
                    {required && <span className="text-red-500">*</span>}
                </p>
            )}
            {empty && !offProfile ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    You haven't added any classes to your profile yet — add the classes you teach under
                    <span className="font-semibold"> Marketplace → Profile</span>.
                </p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {classes.map((c) => {
                        const active = c === value;
                        return (
                            <button
                                type="button"
                                key={c}
                                onClick={() => onChange(active ? "" : c)}
                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${
                                    active
                                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                                }`}
                            >
                                {c}
                            </button>
                        );
                    })}
                    {offProfile && (
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            title="Not part of your profile — tap to clear"
                            className="px-3 py-1.5 rounded-full text-sm font-semibold border bg-amber-50 text-amber-700 border-amber-300 inline-flex items-center gap-1.5"
                        >
                            {value}
                            <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">not in profile</span>
                        </button>
                    )}
                </div>
            )}
            {offProfile && (
                <p className="text-[11px] text-amber-600 mt-1.5">
                    “{value}” isn't part of your profile. Add it under Marketplace → Profile, or pick one of your classes.
                </p>
            )}
            {hint && <p className="text-[11px] text-gray-400 mt-1.5">{hint}</p>}
        </div>
    );
};

// Amber banner shown on the authoring pages when a teacher hasn't finished their
// "What you teach" profile (no subjects and/or no classes). Content is tagged
// from that profile, so we steer them there rather than letting them pick a
// subject/class they don't teach. `navigate` + optional `onProfile` route them
// to Marketplace → Profile.
export const ProfileSetupNotice = ({ missingSubjects, missingClasses, onSetup }) => {
    const missing =
        missingSubjects && missingClasses ? "the subjects and classes you teach"
            : missingSubjects ? "the subjects you teach"
                : "the classes you teach";
    return (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 text-sm">
                <p className="font-semibold">Set up your teaching profile first</p>
                <p className="text-amber-700 mt-0.5">
                    Lessons are tagged with {missing}. Add {missing} to your profile so you can choose from them here.
                </p>
            </div>
            {onSetup && (
                <button
                    onClick={onSetup}
                    className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                >
                    Set up what you teach
                </button>
            )}
        </div>
    );
};

// Content-type picker as icon cards (uses TYPE_META's icon + gradient). Much
// finer than a <select>, and shows the same visual language as the feed tiles.
export const TypePicker = ({ label, value, onChange }) => (
    <div>
        {label && <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(TYPE_META).map(([key, meta]) => {
                const active = key === value;
                const { Icon } = meta;
                return (
                    <button
                        type="button"
                        key={key}
                        onClick={() => onChange(key)}
                        className={`flex items-center gap-2 rounded-xl border p-2.5 text-left transition ${
                            active
                                ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50/40"
                                : "border-gray-200 hover:border-gray-300"
                        }`}
                    >
                        <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.gradient} text-white flex items-center justify-center shrink-0`}>
                            <Icon size={14} />
                        </span>
                        <span className={`text-xs font-semibold leading-tight ${active ? "text-blue-700" : "text-gray-600"}`}>
                            {meta.label}
                        </span>
                    </button>
                );
            })}
        </div>
    </div>
);

// Reusable chip multi-select: pick from `options`, or (when allowCustom) type a
// value and press Enter / tap "Add". Selected values render as removable chips.
// Shared by the teacher content editor + profile (subjects/classes it's for).
export const TagMultiSelect = ({
    label, hint, options = [], value = [], onChange,
    allowCustom = true, placeholder = "Type to search…",
}) => {
    const [input, setInput] = React.useState("");
    const add = (v) => {
        const val = String(v).trim();
        if (!val || value.includes(val)) { setInput(""); return; }
        onChange([...value, val]);
        setInput("");
    };
    const remove = (v) => onChange(value.filter((x) => x !== v));

    const needle = input.trim().toLowerCase();
    const suggestions = options
        .filter((o) => !value.includes(o) && o.toLowerCase().includes(needle))
        .slice(0, 8);
    const canAddCustom = allowCustom && needle && !options.some((o) => o.toLowerCase() === needle);

    return (
        <div>
            {label && <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>}
            {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {value.map((v) => (
                        <span key={v} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                            {v}
                            <button type="button" onClick={() => remove(v)} className="text-blue-600 hover:text-blue-800" aria-label={`Remove ${v}`}>
                                <FaTimes size={10} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        if (canAddCustom || suggestions.length) add(canAddCustom ? input : suggestions[0]);
                    }
                }}
                placeholder={placeholder}
                className="border rounded-lg p-2 w-full text-sm"
            />
            {(suggestions.length > 0 || canAddCustom) && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {suggestions.map((s) => (
                        <button type="button" key={s} onClick={() => add(s)}
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                            + {s}
                        </button>
                    ))}
                    {canAddCustom && (
                        <button type="button" onClick={() => add(input)}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                            + Add "{input.trim()}"
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Single-select topic combobox fed by the DB topic taxonomy
// (/api/quiz/metadata -> topicsBySubject[subject]). The teacher can pick a saved
// topic OR type their own (the bank may be thin for some subjects) — "use the
// topics in the database" without being locked to it. `value` is a plain string.
export const TopicSelect = ({
    label, hint, subject, topicsBySubject = {}, value = "", onChange,
    placeholder = "Pick a topic below, or type your own",
}) => {
    const all = topicsBySubject[subject] || [];
    const needle = (value || "").trim().toLowerCase();
    // Chips are driven by the chosen subject: show its topics, narrowing to
    // matches once the teacher starts typing.
    const chips = all.filter((t) => !needle || t.toLowerCase().includes(needle)).slice(0, 12);

    return (
        <div>
            {label && <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>}
            {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}

            <div className={`flex items-center gap-2 rounded-xl border p-2 transition ${
                subject
                    ? "border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100"
                    : "border-gray-200 bg-gray-50"
            }`}>
                <FaHashtag size={13} className="text-gray-400 shrink-0" />
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={subject ? placeholder : "Choose a subject first"}
                    disabled={!subject}
                    className="w-full text-sm outline-none bg-transparent disabled:cursor-not-allowed"
                />
                {value && (
                    <button type="button" onClick={() => onChange("")} className="text-gray-300 hover:text-gray-500 shrink-0" aria-label="Clear topic">
                        <FaTimes size={12} />
                    </button>
                )}
            </div>

            {subject && chips.length > 0 && (
                <div className="mt-2">
                    <p className="text-[11px] text-gray-400 mb-1.5">Topics in {subject}</p>
                    <div className="flex flex-wrap gap-1.5">
                        {chips.map((t) => {
                            const active = t.toLowerCase() === needle;
                            return (
                                <button type="button" key={t} onClick={() => onChange(active ? "" : t)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                        active
                                            ? "bg-blue-600 text-white border-blue-600"
                                            : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                                    }`}>
                                    {t}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {subject && all.length === 0 && (
                <p className="text-[11px] text-gray-400 mt-1.5">No saved topics for {subject} yet — type your own above.</p>
            )}
        </div>
    );
};

// Inline player/reader for a single segment attachment. The URL is the signed,
// expiring `media_url` the API mints for this viewer — the raw file path is no
// longer reachable — and SecureMedia picks the in-app renderer for its kind
// (canvas for PDFs, converted HTML for Word, an inline player for video), none
// of which offer a download or print control. Wrap the surrounding page in
// <ProtectedArea> to add the watermark and copy/print blocking.
export const SegmentMedia = ({ url, kind, documentUrl, title = "" }) => (
    <SecureMedia url={url} kind={kind} documentUrl={documentUrl} title={title} />
);

// One segment in the reader. When `segment.locked` (a non-subscriber past the
// free first segment) it shows a lock + subscribe CTA instead of the content.
export const SegmentViewer = ({ segment, index, onSubscribe, canSubscribe, subscribing, price, teacherName }) => {
    if (segment.locked) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center">
                <div className="flex items-center gap-2 justify-center text-gray-800 font-semibold">
                    <span className="text-xs text-gray-400">#{index + 1}</span> {segment.title}
                </div>
                <FaLock className="mx-auto mt-3 text-gray-400" size={22} />
                <p className="text-sm text-gray-500 mt-2">
                    Subscribe to unlock all of {teacherName || "this teacher"}'s content
                    {price != null ? ` · ₦${Number(price).toLocaleString()}/mo` : ""}.
                </p>
                {canSubscribe && onSubscribe && (
                    <button onClick={onSubscribe} disabled={subscribing}
                        className="mt-3 bg-blue-600 text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">
                        {subscribing ? "Redirecting…" : "Subscribe"}
                    </button>
                )}
            </div>
        );
    }
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-400">#{index + 1}</span>
                <h3 className="font-semibold text-gray-900">{segment.title}</h3>
                {segment.topic && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">{segment.topic}</span>}
            </div>
            {segment.media_url && (
                <SegmentMedia url={segment.media_url} kind={segment.attachment_kind}
                    documentUrl={segment.document_url} title={segment.title} />
            )}
            {segment.body && <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{segment.body}</p>}
            {!segment.body && !segment.media_url && <p className="text-sm text-gray-400 italic">No content in this segment yet.</p>}
        </div>
    );
};

export { FaChalkboardTeacher };
